import React, { useMemo, useState } from 'react';
import { getPrice } from '../../../shared/lib/pricing';
import { buildSubscriptionBenefits, formatBillingDate } from './subscriptionUtils';

import { ReactComponent as CheckIcon } from '../../../assets/icons/check.svg';
import { ReactComponent as InfoIcon } from '../../../assets/icons/info.svg';
import { ReactComponent as Spinner } from '../../../assets/icons/spinner.svg';
import { ReactComponent as CloudIcon } from '../../../assets/icons/cloud.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';

const Item = ({text}) => {
  return (
    <div className="plan-benefit">
      <CheckIcon className="plan-benefit-icon" />
      <span>{text}</span>
    </div>
  );
}

const PlanCard = ({
  setSubscriptionsModalVisible,
  subscription,
  numClicks,
  canDownloadInvoices = false,
  onDownloadInvoices,
  onEditInvoiceTemplate = () => {},
}) => {
  const [popupVisible, setPopupVisible] = useState(false);
  const [popup2Visible, setPopup2Visible] = useState(false);

  const totalClicks = subscription?.monthlyUsage ?? 0;
  const amountDue = useMemo(() => {
    if (!subscription || subscription.status !== 'active') return null;
    return subscription.cap === 1000000 ? 'Free' : `$${getPrice(totalClicks).toLocaleString()}`;
  }, [subscription]);
  const monthlyCap = useMemo(() => {
    if (!subscription || subscription.status !== 'active') return null;
    return subscription.cap === 1000000 ? 'Free' : `$${subscription.cap.toLocaleString()}`;
  }, [subscription]);
  const benefits = useMemo(() => buildSubscriptionBenefits(numClicks), [numClicks]);

  const usageRatio = numClicks > 0 ? Math.min(Math.max(totalClicks / numClicks, 0), 1) : 0;
  const usagePercent = usageRatio * 100;
  const remainingClicks = Math.max(numClicks - totalClicks, 0);
  const canUpdateSubscription = subscription && subscription.status !== 'legacy' && subscription.cap !== 1000000;
  const canShowInvoices = canDownloadInvoices && onDownloadInvoices;
  const renderBillingActions = () => {
    if (!canShowInvoices) return null;

    return (
      <div className="plan-section-actions">
        <button
          type="button"
          className="btn btn-action"
          onClick={onDownloadInvoices}
        >
          <CloudIcon className="icon xs" />
          <span>Download Invoices</span>
        </button>
        <button
          type="button"
          className="btn btn-action"
          onClick={onEditInvoiceTemplate}
          aria-label="Edit invoice template"
        >
          <EditIcon className="icon xs" />
          <span>Edit Template</span>
        </button>
      </div>
    );
  };

  return (
    <div className="box-gradient plan-card">
      <div className="inner gradient plan-card-inner">
        {subscription === null && (
          <div className="spinner">
            <Spinner />
          </div>
        )}

        {subscription && subscription.status === 'canceled' && (
          <div className="plan-body">
            <div className="plan-section">
              <div className="plan-section-header">
                <div className="plan-section-title">Billing</div>
                {renderBillingActions()}
              </div>
              <p className='footnote'>No subscription active</p>
            </div>

            {canUpdateSubscription && (
              <div className="plan-section">
                <button
                  className="btn primary plan-cta"
                  onClick={() => {
                    setSubscriptionsModalVisible(true);
                  }}
                >
                  Update
                </button>
              </div>
            )}
          </div>
        )}

        {subscription && subscription.status === 'active' && (
          <div className="plan-body">
            <div className="plan-section">
              <div className="plan-section-header">
                <div className="plan-section-title">Billing</div>
                {renderBillingActions()}
              </div>
              <div className="plan-meta">
                <div className="plan-meta-row">
                  <span className="plan-meta-label">Next bill date</span>
                  <span className="plan-meta-value">{formatBillingDate(subscription.expiry)}</span>
                </div>

                <div className="plan-meta-row relative">
                  {popupVisible && (
                    <div className="tooltip">
                      This is the maximum you will be charged each month.
                    </div>
                  )}

                  <div className="plan-meta-label-group">
                    <span className="plan-meta-label">Monthly cap</span>
                    <InfoIcon className="icon sm info-icon"
                              role="button"
                              tabIndex={0}
                              aria-label="Monthly cap info"
                              onMouseEnter={() => setPopupVisible(true)}
                              onMouseLeave={() => setPopupVisible(false)}
                              onFocus={() => setPopupVisible(true)}
                              onBlur={() => setPopupVisible(false)}
                    />
                  </div>
                  <span className="plan-meta-value">{monthlyCap}</span>
                </div>

                <div className="plan-meta-row relative">
                  {popup2Visible && (
                    <div className="tooltip">
                      This is how much you owe so far.
                    </div>
                  )}

                  <div className="plan-meta-label-group">
                    <span className="plan-meta-label">Amount due so far</span>
                    <InfoIcon className="icon sm info-icon"
                              role="button"
                              tabIndex={0}
                              aria-label="Amount due info"
                              onMouseEnter={() => setPopup2Visible(true)}
                              onMouseLeave={() => setPopup2Visible(false)}
                              onFocus={() => setPopup2Visible(true)}
                              onBlur={() => setPopup2Visible(false)}
                    />
                  </div>
                  <span className="plan-meta-value">{amountDue}</span>
                </div>
              </div>
            </div>

            <div className="plan-section">
              <div className="plan-usage">
                <div className="plan-usage__header">
                  <div className="plan-usage__meta">
                    <div className="plan-section-title">Monthly usage</div>
                    <div className="plan-usage__caption">
                      of {numClicks.toLocaleString()} clicks included
                    </div>
                  </div>
                  <div className="plan-usage__value">
                    {totalClicks.toLocaleString()}
                  </div>
                </div>
                <div className="plan-progress-bar">
                  <div className="plan-progress-fill"
                       style={{width: `${usagePercent}%`}} />
                  <div className="plan-progress-remaining"
                       style={{width: `${100 - usagePercent}%`}} />
                </div>
                <div className="plan-usage__footer">
                  <span className="plan-usage__footnote">{Math.round(usagePercent)}% used</span>
                  <span className="plan-usage__footnote">{remainingClicks.toLocaleString()} remaining</span>
                </div>
              </div>

              {canUpdateSubscription && (
                <button className="btn primary plan-cta"
                        onClick={() => {
                          setSubscriptionsModalVisible(true);
                        }}>
                  Update
                </button>
              )}
            </div>
          </div>
        )}

        {subscription && subscription.status === 'legacy' && (
          <div className="plan-body">
            <div className="plan-section">
              <div className="plan-section-header">
                <div className="plan-section-title">Billing</div>
                {renderBillingActions()}
              </div>
              <p className='footnote'>
                Please contact <a href="mailto:team@polygraph.net">team@polygraph.net</a> about upgrading your subscription to our new pricing plan.
              </p>
            </div>
          </div>
        )}

        <div className="plan-benefits">
          <div className="plan-benefits-title">Plan Benefits</div>
          {benefits.map((benefit) => (
            <Item key={benefit} text={benefit} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default PlanCard;
